import { useSession } from '@/client/lib/auth'
import { Button } from '@/components/ui/button'
import { DashboardMetrics } from '@/client/modules/dashboard/components/DashboardMetrics'
import { QuickActionsBar } from '@/client/modules/dashboard/components/QuickActionsBar'
import { PipelineWidget } from '@/client/modules/dashboard/components/PipelineWidget'
import { RecentActivitySection } from '@/client/modules/dashboard/components/RecentActivitySection'
import { SystemInfoSection } from '@/client/modules/dashboard/components/SystemInfoSection'
import { EmailMetricsWidget } from '@/client/modules/dashboard/components/EmailMetricsWidget'
import { DiscussionsWidget } from '@/client/modules/dashboard/components/DiscussionsWidget'
import { CallsMetrics } from '@/client/modules/calls/components/CallsMetrics'
import { MyTasksTodayWidget } from '@/client/modules/tasks/components/MyTasksTodayWidget'
import { features } from '@/shared/config/features'
import { useHiddenWidgets, type WidgetId } from '@/client/hooks/useHiddenWidgets'
import { X, Eye } from 'lucide-react'

/**
 * Wrapper component that adds a hover-visible hide button to widgets
 */
function HideableWidget({
  id,
  children,
  onHide,
}: {
  id: WidgetId
  children: React.ReactNode
  onHide: (id: WidgetId) => void
}) {
  return (
    <div className="relative group">
      {children}
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity bg-background/80 hover:bg-background"
        onClick={() => onHide(id)}
        title="Hide this widget"
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  )
}

/**
 * Get time-based greeting
 */
function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

/**
 * Main dashboard page
 * Displays overview metrics and recent activity to help users see what's happening
 */
export function DashboardPage() {
  const { data: session } = useSession()
  const { hiddenCount, hide, showAll, isHidden } = useHiddenWidgets()

  return (
    <div className="space-y-6">
      {/* Welcome header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {getGreeting()}, {session?.user?.name?.split(' ')[0] || 'User'}!
          </h1>
          <p className="text-muted-foreground mt-1">
            Here's what's happening with your CRM today.
          </p>
        </div>

        {/* Show hidden widgets button */}
        {hiddenCount > 0 && (
          <Button variant="outline" size="sm" onClick={showAll} className="shrink-0">
            <Eye className="h-4 w-4 mr-2" />
            Show {hiddenCount} hidden {hiddenCount === 1 ? 'widget' : 'widgets'}
          </Button>
        )}
      </div>

      {/* Quick Actions Bar */}
      <QuickActionsBar />

      {/* CRM Metrics */}
      {!isHidden('metrics') && (
        <HideableWidget id="metrics" onHide={hide}>
          <DashboardMetrics />
        </HideableWidget>
      )}

      {/* Secondary widgets row - Pipeline, Tasks, Calls */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Pipeline Widget - always show if deals enabled */}
        {features.deals && !isHidden('pipeline') && (
          <HideableWidget id="pipeline" onHide={hide}>
            <PipelineWidget />
          </HideableWidget>
        )}

        {/* My Tasks Today - conditionally rendered when feature enabled */}
        {features.tasks && !isHidden('tasks') && (
          <HideableWidget id="tasks" onHide={hide}>
            <MyTasksTodayWidget />
          </HideableWidget>
        )}

        {/* Calls Metrics - conditionally rendered when feature enabled */}
        {features.calls && !isHidden('calls') && (
          <HideableWidget id="calls" onHide={hide}>
            <CallsMetrics />
          </HideableWidget>
        )}

        {/* Email Metrics - conditionally rendered when feature enabled */}
        {features.email && !isHidden('email') && (
          <HideableWidget id="email" onHide={hide}>
            <EmailMetricsWidget />
          </HideableWidget>
        )}

        {/* Discussions Widget - conditionally rendered when feature enabled */}
        {features.discussions && !isHidden('discussions') && (
          <HideableWidget id="discussions" onHide={hide}>
            <DiscussionsWidget />
          </HideableWidget>
        )}
      </div>

      {/* Recent Activity - Shows latest items from all modules */}
      {!isHidden('activity') && (
        <HideableWidget id="activity" onHide={hide}>
          <RecentActivitySection />
        </HideableWidget>
      )}

      {/* Collapsible System Info & Feedback */}
      <SystemInfoSection />
    </div>
  )
}
